import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator, TrendingUp, Zap, Grid3x3, DollarSign, AlertCircle, Sun, CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CarregadoresMoneyInput } from "@/components/CarregadoresMoneyInput";
import { calcularParcelaTabelaPrice } from "@/utils/saveCondominiosPropostaUtils";
import { formatBrazilianCurrency } from "@/utils/formatters";

interface Municipality { id: number; municipio: string; uf: string; }
interface State { nome: string; sigla: string; }

const Simulador = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [citiesInSelectedState, setCitiesInSelectedState] = useState<Municipality[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(true);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const [simulationData, setSimulationData] = useState({
    tariffGroup: "",
    baseCalculation: "",
    standardType: "",
    monthlyBill: "",
    monthlyConsumption: "",
    peakConsumption: "",
    offPeakConsumption: "",
    includeCost: "",
    systemType: "",
    selectedState: "",
    selectedCity: null as Municipality | null,
  });
  const [results, setResults] = useState<Record<string, unknown> | null>(null);

  const [financeForm, setFinanceForm] = useState({
    valorTotal: 0,
    entrada: 0,
    numParcelas: 10,
    jurosAm: 0,
  });

  const financeSaldo = Math.max(0, financeForm.valorTotal - financeForm.entrada);
  const financeValorParcela = calcularParcelaTabelaPrice(
    financeSaldo,
    financeForm.jurosAm,
    financeForm.numParcelas,
    12
  );
  const financeTotalParcelas = financeValorParcela * financeForm.numParcelas;
  const financeTotalPago = financeForm.entrada + financeTotalParcelas;
  const financeTotalJuros = Math.max(0, financeTotalPago - financeForm.valorTotal);

  useEffect(() => {
    const fetchStates = async () => {
      setIsLoadingStates(true);
      try {
        const { data, error } = await supabase.from('estados_brasil').select('nome, sigla').order('nome');
        if (error) throw error;
        setStates(data || []);
      } catch (error) {
        toast({ title: "Erro ao carregar estados", variant: "destructive" });
      } finally {
        setIsLoadingStates(false);
      }
    };
    fetchStates();
  }, [toast]);

  useEffect(() => {
    const fetchCities = async (sigla: string) => {
      setIsLoadingCities(true);
      try {
        const { data, error } = await supabase.from('municipios').select('*').eq('uf', sigla).order('municipio');
        if (error) throw error;
        setCitiesInSelectedState(data || []);
      } catch (error) {
        toast({ title: "Erro ao carregar cidades", variant: "destructive" });
      } finally {
        setIsLoadingCities(false);
      }
    };
    if (simulationData.selectedState) {
      fetchCities(simulationData.selectedState);
      setSimulationData(prev => ({ ...prev, selectedCity: null }));
    } else {
      setCitiesInSelectedState([]);
    }
  }, [simulationData.selectedState, toast]);

  const getIrradiation = async (state: string, municipio?: string) => {
    if (municipio) {
      const { data, error } = await supabase.from('irradiacao').select('"Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez", "Média"').eq('municipio_id', citiesInSelectedState.find(c => c.municipio === municipio)?.id).single();
      if (error || !data) {
        throw new Error('Falha ao obter dados de irradiação');
      }
      const typedData = data as Record<string, unknown>;
      const parseValue = (val: unknown) => {
        if (typeof val === 'string') {
          return parseFloat(val.replace(',', '.'));
        } else if (typeof val === 'number') {
          return val;
        } else {
          return NaN;
        }
      };
      const months = [
        parseValue(typedData.Jan),
        parseValue(typedData.Fev),
        parseValue(typedData.Mar),
        parseValue(typedData.Abr),
        parseValue(typedData.Mai),
        parseValue(typedData.Jun),
        parseValue(typedData.Jul),
        parseValue(typedData.Ago),
        parseValue(typedData.Set),
        parseValue(typedData.Out),
        parseValue(typedData.Nov),
        parseValue(typedData.Dez)
      ];
      const media = parseValue(typedData.Média);
      if (months.some(m => isNaN(m)) || isNaN(media)) {
        throw new Error('Dados de irradiação inválidos ou incompletos');
      }
      return { monthly: months, psh: media };
    }
    throw new Error('Município não selecionado');
  };

  const getPriceRange = (systemType: string, kWp: number) => {
    let ranges;
    if (systemType === "telhado") {
      ranges = [
        { min: 0, max: 5, minPrice: 2.90, maxPrice: 3.60 },
        { min: 5.01, max: 10, minPrice: 2.30, maxPrice: 2.95 },
        { min: 10.1, max: 20, minPrice: 2.00, maxPrice: 2.50 },
        { min: 20.1, max: 75, minPrice: 1.80, maxPrice: 2.40 },
        { min: 75.1, max: 300, minPrice: 1.75, maxPrice: 2.30 },
        { min: 300.01, max: Infinity, minPrice: 1.70, maxPrice: 2.30 }
      ];
    } else if (systemType === "solo") {
      ranges = [
        { min: 0, max: 5, minPrice: 3.50, maxPrice: 5.60 },
        { min: 5.01, max: 10, minPrice: 3.00, maxPrice: 3.95 },
        { min: 10.1, max: 20, minPrice: 2.80, maxPrice: 3.50 },
        { min: 20.1, max: 75, minPrice: 2.40, maxPrice: 3.10 },
        { min: 75.1, max: 300, minPrice: 2.30, maxPrice: 3.00 },
        { min: 300.01, max: Infinity, minPrice: 2.20, maxPrice: 2.90 }
      ];
    } else {
      return null;
    }

    const range = ranges.find(r => kWp >= r.min && kWp <= r.max);
    return range ? { minPrice: range.minPrice, maxPrice: range.maxPrice } : null;
  };

  const calculateSavings = async () => {
    if (!simulationData.tariffGroup || !simulationData.selectedCity || !simulationData.includeCost) {
      toast({ title: "Campos obrigatórios faltando", variant: "destructive" });
      return;
    }
    if (simulationData.includeCost === "sim" && !simulationData.systemType) {
      toast({ title: "Preencha o tipo do sistema", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const irradiationData = await getIrradiation(simulationData.selectedState, simulationData.selectedCity.municipio);
      const monthlyIrr = irradiationData.monthly;
      const psh = irradiationData.psh;
      const eficiencia = 0.74;
      const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

      let annualGenPerKwp = 0;
      for (let m = 0; m < 12; m++) {
        const monthlyGen = monthlyIrr[m] * daysPerMonth[m] * eficiencia;
        annualGenPerKwp += monthlyGen;
      }

      if (annualGenPerKwp === 0) {
        throw new Error('Geração anual calculada é zero. Verifique os dados de irradiação.');
      }

      const panelKW = 600 / 1000;

      let kWp = 0;
      if (simulationData.tariffGroup === "grupo-b") {
        if (!simulationData.baseCalculation || !simulationData.standardType) {
          toast({ title: "Preencha base de cálculo e padrão", variant: "destructive" });
          return;
        }
        const minKwhMap = { monofasico: 30, bifasico: 50, trifasico: 100 };
        const minKwh = minKwhMap[simulationData.standardType as keyof typeof minKwhMap] || 0;
        let consumo = 0;
        if (simulationData.baseCalculation === "fatura") {
          if (!simulationData.monthlyBill) {
            toast({ title: "Preencha valor da conta", variant: "destructive" });
            return;
          }
          const clean = simulationData.monthlyBill.replace(/\./g, '').replace(',', '.');
          const valorConta = parseFloat(clean);
          if (isNaN(valorConta) || valorConta <= 0) {
            toast({ title: "Valor da conta inválido", variant: "destructive" });
            return;
          }
          const tarifa = 0.81; // Default simplificado (TE + TUSD)
          consumo = valorConta / tarifa;
        } else if (simulationData.baseCalculation === "consumo") {
          if (!simulationData.monthlyConsumption) {
            toast({ title: "Preencha consumo em kWh", variant: "destructive" });
            return;
          }
          consumo = parseFloat(simulationData.monthlyConsumption);
        }
        if (consumo < minKwh) {
          toast({ title: "Consumo inválido", variant: "destructive" });
          return;
        }
        const annualConsAdjusted = 12 * (consumo - minKwh);
        kWp = annualConsAdjusted / annualGenPerKwp;
      } else {
        if (!simulationData.peakConsumption || !simulationData.offPeakConsumption) {
          toast({ title: "Preencha consumos", variant: "destructive" });
          return;
        }
        const consumoPonta = parseFloat(simulationData.peakConsumption);
        const consumoFora = parseFloat(simulationData.offPeakConsumption);
        const effectiveConsumoMonthly = consumoFora + consumoPonta * 3;
        const annualCons = effectiveConsumoMonthly * 12;
        kWp = annualCons / annualGenPerKwp;
      }

      if (isNaN(kWp)) {
        throw new Error('Resultado do cálculo é inválido (NaN). Verifique os dados de entrada.');
      }

      const numPlacas = Math.ceil(kWp / panelKW);

      let approximateCostMin = null;
      let approximateCostMax = null;
      if (simulationData.includeCost === "sim") {
        const priceRange = getPriceRange(simulationData.systemType, kWp);
        if (priceRange) {
          const minCost = priceRange.minPrice * kWp * 1000;
          const maxCost = priceRange.maxPrice * kWp * 1000;
          approximateCostMin = minCost.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          approximateCostMax = maxCost.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      }

      setResults({ systemPowerKWp: kWp.toFixed(2), numPlacas, approximateCostMin, approximateCostMax, psh: psh.toFixed(2), annualGenPerKwp: annualGenPerKwp.toFixed(2) });
      toast({ title: "Cálculo concluído!" });
    } catch (error: unknown) {
      toast({ title: "Erro ao obter dados de irradiação", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMonthlyBillBlur = () => {
    const value = simulationData.monthlyBill;
    if (!value) return;
    const clean = value.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(clean);
    if (isNaN(num)) {
      setSimulationData(prev => ({ ...prev, monthlyBill: '' }));
      toast({ title: "Valor inválido", variant: "destructive" });
    } else {
      const formatted = num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      setSimulationData(prev => ({ ...prev, monthlyBill: formatted }));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-3xl font-bold">Calculadoras</h1>

      <Tabs defaultValue="solar" className="space-y-4">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 h-auto min-h-[2.5rem]">
          <TabsTrigger value="solar" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
            Simulador Solar
          </TabsTrigger>
          <TabsTrigger value="financeira" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
            Calculadora Financeira
          </TabsTrigger>
        </TabsList>

        <TabsContent value="solar" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Calcule sua Instalação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* UI: Grupo Tarifário */}
          <Label>Informe o grupo tarifário</Label>
          <Select value={simulationData.tariffGroup} onValueChange={v => setSimulationData(prev => ({ ...prev, tariffGroup: v }))}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="grupo-b">Grupo B</SelectItem>
              <SelectItem value="grupo-a">Grupo A</SelectItem>
              <SelectItem value="mercado-livre">Mercado Livre</SelectItem>
            </SelectContent>
          </Select>

          {simulationData.tariffGroup === "grupo-b" && (
            <>
              <Label>Informe a forma de cálculo</Label>
              <Select value={simulationData.baseCalculation} onValueChange={v => setSimulationData(prev => ({ ...prev, baseCalculation: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fatura">Valor da Fatura em R$</SelectItem>
                  <SelectItem value="consumo">Consumo em kWh</SelectItem>
                </SelectContent>
              </Select>

              <Label>Informe o tipo do padrão</Label>
              <Select value={simulationData.standardType} onValueChange={v => setSimulationData(prev => ({ ...prev, standardType: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monofasico">Monofásico</SelectItem>
                  <SelectItem value="bifasico">Bifásico</SelectItem>
                  <SelectItem value="trifasico">Trifásico</SelectItem>
                </SelectContent>
              </Select>

              {simulationData.baseCalculation === "fatura" && (
                <>
                  <Label>Informe o valor da conta mensal (R$)</Label>
                  <Input 
                    value={simulationData.monthlyBill} 
                    onChange={e => setSimulationData(prev => ({ ...prev, monthlyBill: e.target.value }))} 
                    onBlur={handleMonthlyBillBlur}
                    placeholder="R$ 400,00" 
                  />
                </>
              )}

              {simulationData.baseCalculation === "consumo" && (
                <>
                  <Label>Informe o consumo mensal (kWh)</Label>
                  <Input value={simulationData.monthlyConsumption} onChange={e => setSimulationData(prev => ({ ...prev, monthlyConsumption: e.target.value }))} placeholder="400" />
                </>
              )}
            </>
          )}

          {(simulationData.tariffGroup === "grupo-a" || simulationData.tariffGroup === "mercado-livre") && (
            <>
              <Label>Informe o consumo ponta (kWh)</Label>
              <Input value={simulationData.peakConsumption} onChange={e => setSimulationData(prev => ({ ...prev, peakConsumption: e.target.value }))} placeholder="150" />
              <Label>Informe o consumo fora ponta (kWh)</Label>
              <Input value={simulationData.offPeakConsumption} onChange={e => setSimulationData(prev => ({ ...prev, offPeakConsumption: e.target.value }))} placeholder="300" />
            </>
          )}

          <Label>Você deseja que essa simulação tenha como resposta custo aproximado do sistema?</Label>
          <Select value={simulationData.includeCost} onValueChange={v => setSimulationData(prev => ({ ...prev, includeCost: v, systemType: v === "nao" ? "" : prev.systemType }))}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sim">Sim</SelectItem>
              <SelectItem value="nao">Não</SelectItem>
            </SelectContent>
          </Select>

          {simulationData.includeCost === "sim" && (
            <>
              <Label>O sistema será de que tipo?</Label>
              <Select value={simulationData.systemType} onValueChange={v => setSimulationData(prev => ({ ...prev, systemType: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solo">Solo</SelectItem>
                  <SelectItem value="telhado">Telhado</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}

          {/* Estado e Cidade */}
          <Label>Selecione o estado</Label>
          <Select value={simulationData.selectedState} onValueChange={v => setSimulationData(prev => ({ ...prev, selectedState: v }))}>
            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{states.map(s => <SelectItem key={s.sigla} value={s.sigla}>{s.nome}</SelectItem>)}</SelectContent>
          </Select>

          {simulationData.selectedState && (
            <>
              <Label>Selecione a cidade</Label>
              <Select value={simulationData.selectedCity?.id.toString() || ""} onValueChange={v => {
                const city = citiesInSelectedState.find(c => c.id.toString() === v);
                if (city) setSimulationData(prev => ({ ...prev, selectedCity: city }));
              }}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{citiesInSelectedState.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.municipio}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}

          <Button onClick={calculateSavings} disabled={isLoading} className="w-full">
            {isLoading ? "Calculando..." : "Calcular"}
          </Button>
        </CardContent>
      </Card>

      {results && (
        <Card className="border border-yellow-200 shadow-lg">
          <CardHeader className="bg-yellow-50">
            <CardTitle className="text-2xl font-bold text-yellow-800">Resultados da Simulação</CardTitle>
            <CardDescription className="text-yellow-600">Estimativas baseadas nos dados fornecidos</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Zap className="h-8 w-8 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Potência do Sistema</p>
                  <p className="text-2xl font-bold">{results.systemPowerKWp} kWp</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Grid3x3 className="h-8 w-8 text-blue-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Estimativa de Placas</p>
                  <p className="text-2xl font-bold">{results.numPlacas} (aprox.)</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Sun className="h-8 w-8 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">PSH Médio</p>
                  <p className="text-2xl font-bold">{results.psh} kWh/m²/dia</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <TrendingUp className="h-8 w-8 text-purple-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Geração Anual</p>
                  <p className="text-2xl font-bold">{results.annualGenPerKwp} kWh/kWp/ano</p>
                </div>
              </div>
              {results.approximateCostMin && results.approximateCostMax && (
                <div className="flex items-center gap-4">
                  <DollarSign className="h-8 w-8 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Custo Aproximado</p>
                    <p className="text-2xl font-bold">R$ {results.approximateCostMin} a R$ {results.approximateCostMax}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="bg-gray-50 border-t flex items-center gap-2 text-sm text-muted-foreground p-4">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p>Esses valores são estimativas e podem variar com base em fatores adicionais. Consulte um especialista para uma avaliação precisa.</p>
          </CardFooter>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="financeira" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Parcelamento
              </CardTitle>
              <CardDescription>
                Simule o parcelamento com entrada e até 12x com juros mensais (Tabela Price).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CarregadoresMoneyInput
                id="finance-valor-total"
                label="Valor total (R$)"
                value={financeForm.valorTotal}
                onValueChange={(n) => setFinanceForm((p) => ({ ...p, valorTotal: n }))}
              />
              <CarregadoresMoneyInput
                id="finance-entrada"
                label="Entrada (R$)"
                value={financeForm.entrada}
                onValueChange={(n) => setFinanceForm((p) => ({ ...p, entrada: n }))}
              />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="finance-parc-n">Nº de parcelas</Label>
                  <Select
                    value={String(financeForm.numParcelas)}
                    onValueChange={(v) =>
                      setFinanceForm((p) => ({
                        ...p,
                        numParcelas: parseInt(v, 10) || 1,
                      }))
                    }
                  >
                    <SelectTrigger id="finance-parc-n">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}x
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="finance-parc-juros">Juros (% a.m.)</Label>
                  <Input
                    id="finance-parc-juros"
                    type="number"
                    min={0}
                    step={0.01}
                    value={financeForm.jurosAm === 0 ? "" : financeForm.jurosAm}
                    placeholder="0"
                    onChange={(e) => {
                      const n = parseFloat(e.target.value);
                      setFinanceForm((p) => ({
                        ...p,
                        jurosAm: Number.isFinite(n) && n >= 0 ? n : 0,
                      }));
                    }}
                  />
                </div>
              </div>

              {financeForm.valorTotal > 0 && financeForm.entrada > financeForm.valorTotal && (
                <p className="text-sm text-destructive">
                  A entrada não pode ser maior que o valor total.
                </p>
              )}

              {financeForm.valorTotal > 0 && financeForm.entrada <= financeForm.valorTotal && (
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">Valor da parcela: </span>
                    <span className="font-semibold">
                      {formatBrazilianCurrency(financeValorParcela)}
                    </span>
                    <span className="text-muted-foreground"> ({financeForm.numParcelas}x)</span>
                  </div>
                  <div className="grid gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <span className="text-muted-foreground">Saldo financiado: </span>
                      <span className="font-medium">{formatBrazilianCurrency(financeSaldo)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total das parcelas: </span>
                      <span className="font-medium">{formatBrazilianCurrency(financeTotalParcelas)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total a pagar: </span>
                      <span className="font-medium">{formatBrazilianCurrency(financeTotalPago)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total de juros: </span>
                      <span className="font-medium">{formatBrazilianCurrency(financeTotalJuros)}</span>
                      {financeForm.jurosAm > 0 ? (
                        <span className="text-muted-foreground"> ({financeForm.jurosAm}% a.m.)</span>
                      ) : (
                        <span className="text-muted-foreground"> (sem juros)</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Simulador;
